import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Grid, Button, Divider,
  FormControlLabel, Checkbox, Snackbar, Alert, CircularProgress, Avatar,
  IconButton, Chip, Autocomplete,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PetsIcon from '@mui/icons-material/Pets';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { collection, getDoc, getDocs, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { crearPresupuestoAutomatico } from '../services/presupuestoAutoService';

import { COLORS } from '../theme/constants';
import {
  ASA_OPTIONS, PROCEDURE_CATEGORIES, SURGERY_PRIORITY, PREOP_EXAMS, INIT_SURGERY_REQUEST,
} from '../models/surgeryModel';

const { primary: PRIMARY, teal: TEAL } = COLORS;

export default function SurgeryReferral() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { activeBranchId } = useBranch();
  const urlPatientId = searchParams.get('patientId');
  const urlRequestId = searchParams.get('id');
  const isView = !!urlRequestId;

  const [form, setForm] = useState(INIT_SURGERY_REQUEST);
  const [patient, setPatient] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [arancelProcedures, setArancelProcedures] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cargar paciente, tutor, solicitud previa y catálogo de procedimientos
  useEffect(() => {
    (async () => {
      try {
        const [procSnap, diagSnap, apSnap, aSnap] = await Promise.all([
          getDocs(collection(db, 'surgicalProcedures')),
          getDocs(collection(db, 'diagnoses')),
          getDocs(collection(db, 'procedures')),
          getDocs(collection(db, 'aranceles')),
        ]);
        setProcedures(procSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.active !== false));
        setDiagnoses(diagSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.diagnosis || '').localeCompare(b.diagnosis || '')));
        setArancelProcedures(apSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.active !== false));
        setAranceles(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false));

        let patientIdToLoad = urlPatientId;
        let nextForm = { ...INIT_SURGERY_REQUEST };
        if (urlRequestId) {
          const reqSnap = await getDoc(doc(db, 'surgeryRequests', urlRequestId));
          if (reqSnap.exists()) {
            const req = { id: reqSnap.id, ...reqSnap.data() };
            nextForm = { ...nextForm, ...req };
            patientIdToLoad = req.patientId || patientIdToLoad;
          }
        }

        if (patientIdToLoad) {
          const patSnap = await getDoc(doc(db, 'patients', patientIdToLoad));
          if (patSnap.exists()) {
            const p = { id: patSnap.id, ...patSnap.data() };
            setPatient(p);
            let ownerData = null;
            if (p.ownerId) {
              const oSnap = await getDoc(doc(db, 'owners', p.ownerId));
              if (oSnap.exists()) { ownerData = { id: oSnap.id, ...oSnap.data() }; }
            }
            nextForm = {
              ...nextForm,
              patientId: p.id,
              patientName: p.name || '',
              patientSpecies: p.species || '',
              patientBreed: p.breed || '',
              patientAge: p.age || p.edad || '',
              patientWeight: p.weight || p.peso || '',
              patientSex: p.genero || p.sex || p.sexo || '',
              allergies: p.alergias || '',
              ownerId: p.ownerId || '',
              ownerName: ownerData?.name || ownerData?.fullName || '',
              ownerPhone: ownerData?.celular || ownerData?.phone || '',
              ownerEmail: ownerData?.contact || ownerData?.email || '',
            };
          }
        }
        setForm(nextForm);
      } catch (err) {
        console.error('Error cargando datos de derivación:', err);
        setSnackbar({ open: true, message: 'Error al cargar datos de derivación', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPatientId, urlRequestId]);

  const setField = (name) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleExam = (key) => (e) => {
    setForm((prev) => ({ ...prev, preOpExams: { ...prev.preOpExams, [key]: e.target.checked } }));
  };

  const handleProcedureSelect = (_, value) => {
    if (!value) { setForm((prev) => ({ ...prev, proposedProcedure: '' })); return; }
    if (typeof value === 'string') { setForm((prev) => ({ ...prev, proposedProcedure: value })); return; }
    setForm((prev) => ({
      ...prev,
      proposedProcedure: value.name,
      procedureCategory: value.category || prev.procedureCategory,
      estimatedDurationMin: value.duration || prev.estimatedDurationMin,
    }));
  };

  const handleDiagnosisSelect = (_, value) => {
    if (!value) { setForm((prev) => ({ ...prev, clinicalDiagnosis: '' })); return; }
    if (typeof value === 'string') { setForm((prev) => ({ ...prev, clinicalDiagnosis: value })); return; }
    setForm((prev) => ({ ...prev, clinicalDiagnosis: value.diagnosis || '' }));
  };

  const normalizeStr = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  const crearPresupuestoDesdeSolicitud = async (requestId, requestNumber) => {
    const branchIdForPrice = activeBranchId || userData?.branchId || '';
    const proc = arancelProcedures.find((p) =>
      (p.procedure || p.name || '') === form.proposedProcedure ||
      (p.procedure || '').toLowerCase() === (form.proposedProcedure || '').toLowerCase() ||
      (p.name || '').toLowerCase() === (form.proposedProcedure || '').toLowerCase()
    );
    const prestaciones = Array.isArray(proc?.prestaciones) ? proc.prestaciones : [];
    if (!prestaciones.length) return null;

    return crearPresupuestoAutomatico({
      orderId: requestId,
      orderType: 'surgeryRequest',
      orderNumber: requestNumber || '',
      patientId: form.patientId || '',
      patientName: form.patientName || '',
      patientSpecies: form.patientSpecies || '',
      ownerId: form.ownerId || '',
      ownerName: form.ownerName || '',
      ownerPhone: form.ownerPhone || '',
      branchId: branchIdForPrice,
      vetId: currentUser?.uid || '',
      vetName: userData?.name || currentUser?.email || '',
      items: prestaciones.map((pr) => {
        const arancel = aranceles.find((a) => a.id === pr.id) ||
                        aranceles.find((a) => normalizeStr(a.prestacion) === normalizeStr(pr.prestacion));
        return {
          arancel,
          nombre: pr.prestacion || 'Prestación asociada',
          cantidad: 1,
          precioFallback: 0,
          tipoPrestacionFallback: 'Cirugía',
        };
      }),
      notas: `Generado automáticamente desde solicitud de cirugía ${requestNumber || ''}`,
    });
  };

  const canSubmit = useMemo(() =>
    form.patientId && form.proposedProcedure && form.clinicalDiagnosis && form.indication,
    [form]);

  const handleSubmit = async () => {
    if (isView) return;
    if (!canSubmit) {
      setSnackbar({ open: true, message: 'Completa procedimiento, diagnóstico e indicación quirúrgica.', severity: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        ...form,
        estimatedDurationMin: Number(form.estimatedDurationMin) || 60,
        fastingHours: Number(form.fastingHours) || 12,
        status: 'pending',
        surgeryId: null,
        requestingVetId: currentUser?.uid || '',
        requestingVetName: userData?.name || currentUser?.email || '',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || '',
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'surgeryRequests'), data);
      const presupuesto = await crearPresupuestoDesdeSolicitud(ref.id, '');
      if (presupuesto) {
        await updateDoc(doc(db, 'surgeryRequests', ref.id), {
          presupuestoId: presupuesto.id,
          presupuestoNumber: presupuesto.numero,
          updatedAt: serverTimestamp(),
        });
      }
      // Log de auditoría
      await addDoc(collection(db, 'surgeryRequests', ref.id, 'auditLog'), {
        action: 'request_created',
        details: `Derivación de cirugía creada: ${form.proposedProcedure}${presupuesto ? ` (presupuesto ${presupuesto.numero})` : ''}`,
        performedBy: currentUser?.uid || '',
        performedByName: userData?.name || currentUser?.email || '',
        timestamp: serverTimestamp(),
      });
      setSnackbar({ open: true, message: presupuesto ? 'Derivación y presupuesto creados.' : 'Derivación de cirugía enviada a la lista de trabajo.', severity: 'success' });
      setTimeout(() => navigate(urlPatientId ? `/patients/${urlPatientId}/record` : '/surgery/worklist'), 1200);
    } catch (err) {
      console.error('Error creando derivación:', err);
      setSnackbar({ open: true, message: `Error al enviar la derivación: ${err.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => navigate(urlPatientId ? `/patients/${urlPatientId}/record` : '/surgery/worklist')} sx={{ color: PRIMARY }}>
          <ArrowBackIcon />
        </IconButton>
        <LocalHospitalIcon sx={{ color: PRIMARY, fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={800} color={PRIMARY}>Derivación de Cirugía</Typography>
          <Typography variant="caption" color="text.secondary">
            Datos clínicos para que el coordinador programe la cirugía
          </Typography>
        </Box>
      </Box>

      {/* Paciente */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        {patient ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: TEAL, width: 44, height: 44 }}><PetsIcon /></Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={700}>{patient.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {form.patientSpecies} · {form.patientBreed} · {form.patientAge}
                {form.patientWeight ? ` · ${form.patientWeight} kg` : ''}
                {form.patientSex ? ` · ${form.patientSex}` : ''}
              </Typography>
              {form.ownerName && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Tutor: {form.ownerName} {form.ownerPhone ? `· ${form.ownerPhone}` : ''}
                </Typography>
              )}
            </Box>
            {form.allergies && (
              <Chip label={`⚠ Alergias: ${form.allergies}`} size="small"
                sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }} />
            )}
          </Box>
        ) : (
          <Alert severity="warning">
            No se especificó un paciente. Accede a esta pantalla desde la ficha del paciente.
          </Alert>
        )}
      </Paper>

      {/* Datos clínicos */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Grid container spacing={2.5}>
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Procedimiento propuesto</Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              freeSolo
              options={procedures}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
              value={form.proposedProcedure}
              onChange={handleProcedureSelect}
              onInputChange={(_, v, reason) => { if (reason === 'input') setForm((prev) => ({ ...prev, proposedProcedure: v })); }}
              renderInput={(params) => (
                <TextField {...params} label="Procedimiento quirúrgico *" size="small" required
                  error={!form.proposedProcedure} helperText={!form.proposedProcedure ? 'Obligatorio' : 'Selecciona del catálogo o escribe uno'} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField select label="Categoría" size="small" fullWidth
              value={form.procedureCategory} onChange={setField('procedureCategory')}>
              {PROCEDURE_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField select label="Prioridad *" size="small" fullWidth
              value={form.priority} onChange={setField('priority')}>
              {Object.entries(SURGERY_PRIORITY).map(([k, cfg]) => <MenuItem key={k} value={k}>{cfg.label}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Lateralidad / Región" size="small" fullWidth
              placeholder="Ej: Miembro posterior izquierdo"
              value={form.laterality} onChange={setField('laterality')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField label="Duración estimada (min)" type="number" size="small" fullWidth
              value={form.estimatedDurationMin} onChange={setField('estimatedDurationMin')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField select label="ASA propuesto" size="small" fullWidth
              value={form.asaScore} onChange={setField('asaScore')}>
              <MenuItem value="">Sin evaluar</MenuItem>
              {ASA_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} sx={{ mt: 1 }}>Información clínica</Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid size={12}>
            <Autocomplete
              freeSolo
              options={diagnoses}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.diagnosis || '')}
              value={form.clinicalDiagnosis}
              onChange={handleDiagnosisSelect}
              onInputChange={(_, v, reason) => { if (reason === 'input') setForm((prev) => ({ ...prev, clinicalDiagnosis: v })); }}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter((o) =>
                  (o.diagnosis || '').toLowerCase().includes(q) ||
                  (o.code || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.diagnosis}</Typography>
                    {option.code && (
                      <Typography variant="caption" color="text.secondary">Código: {option.code}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Diagnóstico *" size="small" fullWidth required
                  error={!form.clinicalDiagnosis} helperText="Selecciona del catálogo o escribe uno" />
              )}
              isOptionEqualToValue={(opt, val) => (typeof val === 'string' ? opt.diagnosis === val : opt.id === val.id)}
              noOptionsText="No se encontraron diagnósticos"
            />
          </Grid>
          <Grid size={12}>
            <TextField label="Indicación quirúrgica / justificación *" size="small" fullWidth multiline rows={2} required
              value={form.indication} onChange={setField('indication')}
              error={!form.indication} />
          </Grid>
          <Grid size={12}>
            <TextField label="Resumen clínico / anamnesis" size="small" fullWidth multiline rows={3}
              value={form.clinicalSummary} onChange={setField('clinicalSummary')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Comorbilidades / riesgos anestésicos" size="small" fullWidth multiline rows={2}
              value={form.comorbidities} onChange={setField('comorbidities')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Medicación actual" size="small" fullWidth multiline rows={2}
              value={form.currentMedications} onChange={setField('currentMedications')} />
          </Grid>
          <Grid size={12}>
            <TextField label="Alergias conocidas" size="small" fullWidth
              value={form.allergies} onChange={setField('allergies')} />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} sx={{ mt: 1 }}>Exámenes preoperatorios solicitados</Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>
          <Grid size={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {PREOP_EXAMS.map((ex) => (
                <FormControlLabel key={ex.key}
                  control={<Checkbox checked={!!form.preOpExams[ex.key]} onChange={toggleExam(ex.key)} />}
                  label={ex.label} />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} sx={{ mt: 1 }}>Logística y preferencias</Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField label="Ayuno indicado (h)" type="number" size="small" fullWidth
              value={form.fastingHours} onChange={setField('fastingHours')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4.5 }}>
            <TextField label="Fecha preferida desde" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.preferredDateFrom} onChange={setField('preferredDateFrom')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4.5 }}>
            <TextField label="Fecha preferida hasta" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.preferredDateTo} onChange={setField('preferredDateTo')} />
          </Grid>
          <Grid size={12}>
            <TextField label="Requerimientos especiales / equipamiento" size="small" fullWidth
              placeholder="Ej: torre de laparoscopía, implantes, banco de sangre"
              value={form.specialRequirements} onChange={setField('specialRequirements')} />
          </Grid>
          <Grid size={12}>
            <TextField label="Notas para el coordinador" size="small" fullWidth multiline rows={2}
              value={form.notes} onChange={setField('notes')} />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={<Checkbox checked={form.consentObtained} onChange={(e) => setForm((p) => ({ ...p, consentObtained: e.target.checked }))} />}
              label="Consentimiento informado conversado con el tutor" />
          </Grid>

          <Grid size={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate(urlPatientId ? `/patients/${urlPatientId}/record` : '/surgery/worklist')}>
                {isView ? 'Volver' : 'Cancelar'}
              </Button>
              {!isView && (
                <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit}
                  disabled={submitting || !patient}
                  sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
                  {submitting ? 'Enviando…' : 'Enviar derivación'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
